import { useCallback, useEffect, useState, type FC } from "react";
import { CodeXmlIcon, CrownIcon, TicketIcon } from "lucide-react";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/ds/shadcn/components/ui/input-group";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { usePlans } from "@/features/subscriptions/api/usePlans";
import { usePlanForPromoCode } from "@/features/subscriptions/api/usePlanForPromoCode";
import type { PlanModel } from "@/features/subscriptions/api/models/PlanModel";
import { getInitialPlan, type PlanPreference } from "@/features/subscriptions/SubscriptionLogic";
import { SelectableOption } from "@/features/subscriptions/components/SelectableOption";
import { IconTile } from "@/features/subscriptions/components/IconTile";

export interface PlanSelection {
	readonly plan: PlanModel;
	readonly promoCode: string | null;
}

export interface PlanPickerProps {
	value: PlanSelection | null;
	onChange: (value: PlanSelection) => void;
	planPreference: PlanPreference;
	preselectedPlanId: string | null;
	initialPromoCode?: string;
	disabled?: boolean;
}

export const PlanPicker: FC<PlanPickerProps> = props => {
	const {
		value,
		onChange,
		planPreference,
		preselectedPlanId,
		initialPromoCode,
		disabled = false
	} = props;

	const plans = usePlans();
	const planForPromoCode = usePlanForPromoCode();

	const [promoCode, setPromoCode] = useState(initialPromoCode ?? "");
	const [promoSelection, setPromoSelection] = useState<PlanSelection | null>(null);
	const trimmedPromoCode = promoCode.trim();

	useEffect(() => {
		if (value || !plans.data) {
			return;
		}

		const plan = getInitialPlan(plans.data, planPreference, preselectedPlanId);
		if (plan) {
			onChange({ plan, promoCode: null });
		}
	}, [plans.data]);

	useEffect(() => {
		if (initialPromoCode) {
			planForPromoCode.mutate(initialPromoCode);
		}
	}, []);

	useEffect(() => {
		if (!planForPromoCode.data || !planForPromoCode.variables) {
			return;
		}

		const selection: PlanSelection = {
			plan: planForPromoCode.data.body,
			promoCode: planForPromoCode.variables
		};

		setPromoSelection(selection);
		onChange(selection);
	}, [planForPromoCode.data]);

	const onApplyPromoCode = useCallback(() => {
		if (!trimmedPromoCode) {
			return;
		}

		planForPromoCode.mutate(trimmedPromoCode);
	}, [trimmedPromoCode, planForPromoCode.mutate]);

	if (plans.isLoading) {
		return (
			<div className="flex justify-center p-3">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<ErrorDisplay error={plans.error} />

			<div role="radiogroup" aria-label="Plan" className="flex flex-col gap-2">
				{
					plans.data?.map(x => (
						<SelectableOption
							key={x.planId}
							media={<IconTile>{x.clientCredentialsAccess ? <CodeXmlIcon /> : <CrownIcon />}</IconTile>}
							title={x.name}
							subtitle={<MoneyDisplay mode="Standard" money={{ value: x.price, currency: "USD" }} />}
							selected={!!value && value.promoCode === null && value.plan.planId === x.planId}
							disabled={disabled}
							onSelect={() => onChange({ plan: x, promoCode: null })} />
					))
				}

				{
					promoSelection && (
						<SelectableOption
							media={<IconTile><TicketIcon /></IconTile>}
							title={promoSelection.plan.name}
							subtitle={
								<div className="flex gap-1">
									<MoneyDisplay mode="Standard" money={{ value: promoSelection.plan.price, currency: "USD" }} />
									<span className="truncate">· {promoSelection.promoCode}</span>
								</div>
							}
							selected={!!value && value.promoCode === promoSelection.promoCode && value.plan.planId === promoSelection.plan.planId}
							disabled={disabled}
							onSelect={() => onChange(promoSelection)} />
					)
				}
			</div>

			<InputGroup>
				<InputGroupInput
					placeholder="Promo code"
					aria-label="Promo code"
					value={promoCode}
					disabled={disabled}
					onChange={e => setPromoCode(e.currentTarget.value)}
					onKeyDown={e => e.key === "Enter" && onApplyPromoCode()} />
				<InputGroupAddon align="inline-end">
					<InputGroupButton disabled={disabled || !trimmedPromoCode || planForPromoCode.isPending} onClick={onApplyPromoCode}>
						{planForPromoCode.isPending ? <Spinner /> : "Apply"}
					</InputGroupButton>
				</InputGroupAddon>
			</InputGroup>

			<ErrorDisplay error={planForPromoCode.error} subtle />
		</div>
	);
};
